import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import {
    collectSnapshotCompareLegendUsers,
    getSnapshotCompareUserColor,
    resolveSnapshotCompareDiffColorOptions,
    type SnapshotCompareDiffColorOptions,
    type SnapshotCompareLegendUser,
} from '@/lib/snapshot/snapshot-user';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';

type SnapshotCompareUserLegendProps = {
    editor?: Editor | null;
};

export function SnapshotCompareUserLegend({ editor: providedEditor }: SnapshotCompareUserLegendProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const state = useEditorState({
        editor,
        selector: ({ editor: ed }) => {
            if (!ed?.storage.snapshotCompare?.isPreviewing) {
                return { isDiffing: false, colorByUserId: false, users: [] as SnapshotCompareLegendUser[] };
            }

            const storage = ed.storage.snapshotCompare as {
                diffs?: Array<{ attribution: { userId?: string | null; userName?: string } }>;
                diffColorOptions?: SnapshotCompareDiffColorOptions;
            };
            const { colorByUserId } = resolveSnapshotCompareDiffColorOptions(storage.diffColorOptions);

            return {
                isDiffing: true,
                colorByUserId,
                users: collectSnapshotCompareLegendUsers(storage.diffs ?? []),
            };
        },
    }) ?? { isDiffing: false, colorByUserId: false, users: [] as SnapshotCompareLegendUser[] };

    if (!state.isDiffing || !state.colorByUserId || state.users.length === 0) {
        return null;
    }

    return (
        <div
            className="flex shrink-0 flex-wrap gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2"
            aria-label="Diff contributors"
        >
            {state.users.map(user => (
                <div
                    key={user.userId ?? 'Unknown'}
                    className="flex items-center gap-1.5 text-xs text-gray-700"
                >
                    <span
                        className="size-3 shrink-0 rounded-sm"
                        style={{
                            backgroundColor: getSnapshotCompareUserColor(user.userId),
                        }}
                    />
                    {user.displayName}
                </div>
            ))}
        </div>
    );
}

export default SnapshotCompareUserLegend;
